/**
 * Fit a Section 2 document title into the printed I-9's Document Title cell (VTK-1896).
 *
 * The cell is a fixed ~138-unit box in the page SVG and the value used to be split by a
 * 35-character threshold — but Arial weight-1000 at 9px fits only ~23 characters, so titles like
 * the pre-split "U.S. Passport or Passport Card" took the single-line branch and overran into the
 * neighboring column, while very long titles (the Form I-94 receipt label is ~100 characters)
 * overran even the two-line branch, whose second line was unbounded.
 *
 * Policy, by measured width: one line at 9px, then 8px; two lines at 7px, then 6px (break at a
 * space, both lines must fit); floor: two lines at 6px with the overflow elided — the box has a
 * real capacity limit and printing across the Issuing Authority / List B column reads as a corrupt
 * form, which is worse than an ellipsis. The backend corrected-I9 generator applies the same
 * policy with exact pdf-lib font metrics (corrected-i9-pdf.service.ts).
 */

export interface FittedDocumentTitle {
  line1: string;
  line2: string;
  fontSize: number;
}

/** Measures rendered text width in SVG user units (≈ CSS px at these sizes). */
export type TextMeasurer = (text: string, fontSize: number) => number;

/**
 * Usable width of a document-title cell. The pre-printed box is ~138 units wide starting at
 * x=126 and the value starts at x=130, leaving ~134; 132 keeps a hair of right padding.
 */
export const TITLE_BOX_WIDTH = 132;

const SINGLE_LINE_SIZES = [9, 8];
const TWO_LINE_SIZES = [7, 6];
const FLOOR_SIZE = 6;

/**
 * Approximate per-character advance widths (em) for Arial at the heavy weight the form uses,
 * with a small safety factor. Only the width CLASS matters here — the exact glyph widths come
 * from the canvas measurer in a real browser; this keeps the decision deterministic in jsdom.
 */
function estimateCharEm(ch: string): number {
  if (/[mwMW@]/.test(ch)) return 0.94;
  if (/[A-Z0-9]/.test(ch)) return 0.72;
  if (/[ilj.,'’!|:;\s]/.test(ch)) return 0.28;
  if (/[tfr\-()/]/.test(ch)) return 0.36;
  return 0.56;
}

const ESTIMATE_SAFETY = 1.06;

export function estimateTextWidth(text: string, fontSize: number): number {
  let em = 0;
  for (const ch of text) em += estimateCharEm(ch);
  return em * fontSize * ESTIMATE_SAFETY;
}

let canvasCtx: CanvasRenderingContext2D | null | undefined;

/**
 * Default measurer: canvas measureText with the cell's actual font where the browser provides it,
 * falling back to the estimate (jsdom has no 2D context; canvas mocks measure everything as 0,
 * which would make every title "fit" at 9px — the zero-width guard catches both).
 */
function measureText(text: string, fontSize: number): number {
  if (canvasCtx === undefined) {
    try {
      canvasCtx = document.createElement('canvas').getContext('2d');
    } catch {
      canvasCtx = null;
    }
  }
  if (canvasCtx) {
    canvasCtx.font = `1000 ${fontSize}px Arial, Helvetica, sans-serif`;
    const width = canvasCtx.measureText(text).width;
    if (width > 0 || text.length === 0) return width;
  }
  return estimateTextWidth(text, fontSize);
}

/** Greedy space-wrap into two lines; null when no split gives two lines that both fit. */
function wrapIntoTwoLines(
  title: string,
  fontSize: number,
  maxWidth: number,
  measure: TextMeasurer
): [string, string] | null {
  const words = title.split(' ').filter(Boolean);
  if (words.length < 2) return null;
  let line1 = '';
  let restFrom = 0;
  for (let i = 1; i < words.length; i++) {
    const candidate = words.slice(0, i).join(' ');
    if (measure(candidate, fontSize) > maxWidth) break;
    line1 = candidate;
    restFrom = i;
  }
  if (!line1) return null;
  const line2 = words.slice(restFrom).join(' ');
  if (measure(line2, fontSize) > maxWidth) return null;
  return [line1, line2];
}

/** Elide with '…' to the width (measured), leaving text that fits untouched. */
function elide(text: string, fontSize: number, maxWidth: number, measure: TextMeasurer): string {
  if (measure(text, fontSize) <= maxWidth) return text;
  let out = text;
  while (out.length > 1 && measure(`${out}…`, fontSize) > maxWidth) {
    out = out.slice(0, -1);
  }
  return `${out}…`;
}

export function fitDocumentTitle(
  title: string | undefined | null,
  measure: TextMeasurer = measureText,
  maxWidth: number = TITLE_BOX_WIDTH,
  // A caller whose cell is short can restrict the two-line ladder. Supplement B's row is one value
  // line tall — 16 units between the printed header and the row's bottom rule — so it takes two
  // lines only at 6px, where they measure inside the rule. Section 2's cell is a tall stacked
  // column and keeps the default.
  twoLineSizes: readonly number[] = TWO_LINE_SIZES
): FittedDocumentTitle {
  if (!title) return { line1: '', line2: '', fontSize: SINGLE_LINE_SIZES[0] };

  for (const fontSize of SINGLE_LINE_SIZES) {
    if (measure(title, fontSize) <= maxWidth) {
      return { line1: title, line2: '', fontSize };
    }
  }
  for (const fontSize of twoLineSizes) {
    const wrapped = wrapIntoTwoLines(title, fontSize, maxWidth, measure);
    if (wrapped) return { line1: wrapped[0], line2: wrapped[1], fontSize };
  }

  // Floor: nothing fits whole. Keep a wrap when there is one and elide the overflow.
  const words = title.split(' ').filter(Boolean);
  if (words.length >= 2) {
    let line1 = words[0];
    let restFrom = 1;
    for (let i = 2; i < words.length; i++) {
      const candidate = words.slice(0, i).join(' ');
      if (measure(candidate, FLOOR_SIZE) > maxWidth) break;
      line1 = candidate;
      restFrom = i;
    }
    return {
      line1: elide(line1, FLOOR_SIZE, maxWidth, measure),
      line2: elide(words.slice(restFrom).join(' '), FLOOR_SIZE, maxWidth, measure),
      fontSize: FLOOR_SIZE,
    };
  }
  return {
    line1: elide(title, FLOOR_SIZE, maxWidth, measure),
    line2: '',
    fontSize: FLOOR_SIZE,
  };
}
